import { CardImages, CardNetworkIconSmall } from "@/modules/card/common/constants";
import type { ICard } from "@/modules/card/common/types";
import { CardStatus } from "@/modules/card/components/Cards/CardStatus";
import { useLazyGetCardBalanceQuery } from "@/modules/card/services";
import { CloudflareAsset } from "@/shared/common/enums";
import { Amount, CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { formatCardNumber } from "@/shared/helpers";
import { useAppSelector } from "@/shared/hooks";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import classNames from "classnames";
import { type MouseEvent, useState } from "react";

interface Props extends ICard {
  handleShowBalances: (i: number) => void;
  index: number;
  hasMoreAction?: boolean,
  rounded?: boolean,
  className?: string;
}

export const CardItem = (
  {
    currency,
    description,
    number,
    index,
    id,
    name,
    handleShowBalances,
    hasMoreAction,
    rounded = false,
    cardClassification,
    cardNetwork,
    className,
  }: Props) => {
  const showBalances = useAppSelector((state) => state.dashboard.showBalances);
  const [cardBalance, setCardBalance] = useState<number | null>(null);
  const [getCardBalance] = useLazyGetCardBalanceQuery();

  const onShowBalance = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (showBalances[index]) {
      handleShowBalances(index);
    } else {
      getCardBalance({ cardId: `${id}` }).unwrap().then((balance: number) => {
        setCardBalance(balance || 0);
      }).finally(() => {
        handleShowBalances(index);
      });
    }
  };

  const cardImage = CardImages[cardClassification] || CloudflareAsset.DefaultCard;
  const cardNetworkIcon = CardNetworkIconSmall[cardNetwork];

  return (
    <div className={`flexbox align-items--center pl-32 pr-32 widget-content-item ${className}`}>
      <div className={
        classNames("card-account-image flexbox", { "rounded": rounded })}>
        <CloudFlareImage
          className="card-classification-image"
          path={cardImage}
        />
        {cardNetworkIcon && (
          <CloudFlareImage
            className="card-network-image"
            path={cardNetworkIcon}
          />
        )}
      </div>
      <div className="full-width flexbox flex-direction--column pl-16 card-account-content-wrapper">
        {!!index && (
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        )}
        <div className="flexbox full-width pt-20 pb-20 card-account-body-wrapper">
          <div className="flexbox flex-direction--column card-account-body">
            <div className="full-width flexbox justify-content--between">
              <Text size="medium" weight="semibold" className="pr-16 truncate card-account-name">
                {name || description}
              </Text>
              <div
                onClick={onShowBalance}
                className="flexbox justify-content--center align-items--end flex-direction--column pr-6 card-account-balance">
                {cardBalance === null && showBalances[index] ? (
                  <Text weight="bold">N/A</Text>
                ) : (
                  <Amount
                    currency={currency}
                    amount={cardBalance as number}
                    isShow={showBalances[index]}
                  />
                )}
              </div>
            </div>
            <div className="full-width flexbox justify-content--between">
              <Text className="card-account-number">
                {formatCardNumber({ card: number, isHidden: true })}
              </Text>
              <CardStatus className="pr-6" cardId={id}/>
            </div>
          </div>
          {hasMoreAction && (
            <div className="cursor-pointer flexbox align-items--center">
              <MoreIcon type="secondary"/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
